import { DOCUMENT } from '@angular/common';
import {
  DestroyRef,
  Injectable,
  NgZone,
  Signal,
  computed,
  effect,
  inject,
  signal,
  untracked
} from '@angular/core';
import { Store } from '@ngrx/store';

import { getHandledJobUids } from '@core/models/entities/implementations/user-route-action-v1';
import {
  CarriedItem,
  CarriedJob,
  CarriedStop,
  CarriedTour,
  PlacementTarget,
  PlanEditGesture
} from '@core/models/plan-edit-gesture.model';
import { PlanEditSessionActions } from '@core/store/plan-edit/plan-edit-session.actions';
import {
  selectPlanEditSession,
  selectSessionPins
} from '@core/store/plan-edit/plan-edit-session.selectors';
import { selectPlan } from '@core/store/plan/plan.selectors';
import { jobQuantity } from '@core/utils/plan-edit/job-quantity';
import { moveGesture } from '@core/utils/plan-edit/move-gestures';
import { isPredictedActionUid } from '@core/utils/plan-edit/predictions';
import {
  actionStart,
  departureAction,
  earliestPinStart,
  pinOrigin,
  routeStops
} from '@core/utils/plan-edit/route-timing';
import {
  NEW_ROUTE_ROW,
  loadsJobsDeliveredOnItsRoute,
  newRouteTarget,
  positionsFor
} from '@core/utils/plan-edit/targets';
import { Tour, tourMoveBlocker } from '@core/utils/plan-edit/tours';

import { PlanEditFormsService } from '@features/components/plan/edit/plan-edit-forms/plan-edit-forms.service';

import { MIN_TASK_WIDTH_PX, Task, TimelineRow } from '../models/timeline';
import { TimelineGeometry, dateOfX, snapToMinutes, xOfDate } from './timeline-geometry';

export interface EditDrag {
  /** A tour only ever moves; a route's start only ever slides. */
  mode: 'pin' | 'reorder' | 'move' | 'start';
  carrying: 'stop' | 'tour' | 'start';
  routeUid: string;
  actionUid: string;
  /** Every action carried: the stop's own, or each of the tour's. */
  actionUids: string[];
  /** What the strip and the badge call what is dragged: the stop's site, the tour, or the route. */
  stopName: string;
  /** Where the block or the tour starts on the route as shown. */
  from: Date;
  durationMs: number;
  /** Where the block would start once dropped. */
  time: Date;
  shiftMinutes: number;
  /** The earliest start the block can be pinned to, on the block's axis; `null` when unbounded. */
  earliest: Date | null;
  /**
   * Where a drop can land: the stop's own route while reordering, the route under the pointer
   * while moving; empty while pinning.
   */
  targets: EditTarget[];
  /** One of `targets`, the one a drop would land in. */
  target: EditTarget | null;
  /** The route under the pointer while moving, `NEW_ROUTE_ROW` over the + New route row. */
  overRowUid: string | null;
  /** The stop loads jobs its route delivers, so no other route offers it a place. */
  leaveBlocked: boolean;
}

/** A place a stop can land, drawn where it would land on the timeline. */
export interface EditTarget extends PlacementTarget {
  /** A gap and a route's end are drawn midway between the blocks around them, a merge mid-stop. */
  at: Date;
  /** The stop a gap is before or a merge goes into; `null` for a route's end. */
  stop: { name: string; start: Date; end: Date } | null;
}

/** A stop or one of its jobs picked from its menu, waiting for a click on the place it goes. */
export interface EditPlacing {
  kind: 'stop' | 'job' | 'tour';
  /** The stop carried, the one the carried job leaves, or the tour's first. */
  actionUid: string;
  /** The stops outlined at their place while it is carried: the stop's, the tour's, or the job's. */
  actionUids: string[];
  /** The carried tour is every stop of its route. */
  allStops: boolean;
  /**
   * What the strip and the carried chip call the carried item: the stop's name, the job's uid,
   * the tour as its menu named it.
   */
  name: string;
  /** The name of the stop carried or left, whose site a merge target is at; empty for a tour. */
  site: string;
  /** A carried job's quantity at the stop it leaves; `null` for a stop, or a job moving none. */
  quantity: string | null;
  /** Every shown route's targets, by row. */
  targets: Record<string, EditTarget[]>;
  target: EditTarget | null;
  /** The route under the pointer, whose lane the carried stop follows it across. */
  overRowUid: string | null;
}

/** A route's start marker, dragged to move when the route leaves. */
interface CarriedStart {
  kind: 'start';
  routeUid: string;
}

interface Press {
  row: TimelineRow;
  carried: CarriedStop | CarriedTour | CarriedStart;
  name: string;
  leaveBlocked: boolean;
  clientX: number;
  /** Pointer offset from the block's left edge, so the badge stays under the cursor. */
  grabOffset: number;
  from: Date;
  to: Date;
  origin: Date;
  earliest: Date | null;
  /** The dragged row's band on screen, when the press came from the page. */
  band: { top: number; bottom: number } | null;
  moved: boolean;
  dx: number;
  clientY: number;
  /** Computed on the first switch to reorder, from the row as shown when the press began. */
  reorderTargets: EditTarget[] | null;
  /** Each other route's targets, computed the first time the pointer crosses it. */
  moveTargets: Map<string, EditTarget[]>;
  over: Over | null;
}

/** The task area under the pointer and its route; `row` is `null` on the + New route row. */
interface Over {
  lane: Element;
  row: TimelineRow | null;
}

interface Hover {
  rowUid: string | null;
  target: EditTarget | null;
}

const NO_HOVER: Hover = { rowUid: null, target: null };
const SNAP_MS = 5 * 60_000;
/** The pointer's x in the task area, set on the timeline's host for the badge to follow. */
const POINTER_X_PROPERTY = '--edit-pointer-x';
/** Where a reordered block's left edge follows the pointer, for the same reason. */
const CARRY_X_PROPERTY = '--edit-carry-x';
/** Set on the body while a block is dragged, so the cursor says so wherever the pointer is. */
const GRABBING_CLASS = 'edit-grabbing';
/** Set on the body while a stop is placed: every click either places it or cancels. */
const PLACING_CLASS = 'edit-placing';
/** Movement under this many pixels is a click, which opens the step popup. */
const DRAG_THRESHOLD_PX = 4;
/** A click this close to a gap or a route's end lands there; one farther from every target cancels. */
const PLACE_REACH_PX = 24;
/** Marks a row's task area with its row's uid, where a moved or placed stop can land. */
export const EDIT_ROW_ATTRIBUTE = 'data-edit-row';
/**
 * The + New route row's one target. The row takes a drop anywhere, so it is never drawn or found
 * by its instant.
 */
const NEW_ROUTE_EDIT_TARGET: EditTarget = {
  ...newRouteTarget(),
  at: new Date(0),
  stop: null
};

/**
 * Pointer gestures on the timeline in edit mode. Provided by the timeline, so each window's
 * timeline has its own; the document listeners exist only between a press and its release, or
 * while a stop is placed.
 *
 * The listeners run outside Angular's zone and `drag` and `placing` change only when what they
 * show does — the snapped time while pinning, the hovered target while reordering, moving or
 * placing: in a zone app any change re-checks the whole timeline, and a pointer moves on every
 * frame. The pointer itself reaches the badge and a carried block as CSS properties, which need no
 * change detection at all.
 */
@Injectable()
export class TimelineEditService {
  private readonly store = inject(Store);
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);
  private readonly session = this.store.selectSignal(selectPlanEditSession);
  private readonly plan = this.store.selectSignal(selectPlan);
  private readonly pins = this.store.selectSignal(selectSessionPins);
  private readonly forms = inject(PlanEditFormsService);

  /**
   * Only the tab editing the plan in view drags, and only while the session takes gestures: not
   * while it saves, closes or waits for its connection.
   */
  readonly editingHere = computed(() => {
    const session = this.session();
    return (
      session.mode === 'editing' &&
      session.planId !== null &&
      session.planId === this.plan()?.id
    );
  });

  private geometry: Signal<TimelineGeometry | null> = signal(null);
  /** The rows as the timeline shows them, predictions included: targets come from these. */
  private rows: Signal<TimelineRow[]> = signal([]);
  private host: HTMLElement | null = null;
  private press: Press | null = null;
  private listeners: AbortController | null = null;
  private clickGuard: AbortController | null = null;

  private readonly _drag = signal<EditDrag | null>(null);
  readonly drag = this._drag.asReadonly();
  private readonly _altHeld = signal(false);
  readonly altHeld = this._altHeld.asReadonly();

  private readonly picked = signal<{
    carried: CarriedItem;
    shown: Omit<EditPlacing, 'targets' | 'target' | 'overRowUid'>;
  } | null>(null);
  private readonly hover = signal<Hover>(NO_HOVER);
  private placingListeners: AbortController | null = null;
  /** All rows' task areas share one left edge; read once per lane entered, again after a scroll. */
  private lane: Element | null = null;
  private laneLeft: number | null = null;
  /** Recomputed when the shown rows change, so a patch landing mid-placing moves the targets. */
  private readonly placingTargets = computed(() => {
    const picked = this.picked();
    if (!picked) return null;
    const byRow: Record<string, EditTarget[]> = {};
    for (const row of this.rows()) byRow[row.uid] = drawnTargets(row, picked.carried);
    byRow[NEW_ROUTE_ROW] = [NEW_ROUTE_EDIT_TARGET];
    return byRow;
  });
  readonly placing = computed<EditPlacing | null>(() => {
    const picked = this.picked();
    const targets = this.placingTargets();
    if (!picked || !targets) return null;
    const { rowUid, target } = this.hover();
    return {
      ...picked.shown,
      targets,
      target,
      overRowUid: rowUid
    };
  });

  constructor() {
    // A session closed while idle or superseded elsewhere must not leave a crosshair, or a
    // listener swallowing the next click, behind it.
    effect(() => {
      if (!this.editingHere()) untracked(() => this.cancel());
    });
    inject(DestroyRef).onDestroy(() => {
      this.cancel();
      this.clickGuard?.abort();
    });
  }

  attach(
    geometry: Signal<TimelineGeometry | null>,
    host: HTMLElement,
    rows: Signal<TimelineRow[]>
  ): void {
    this.geometry = geometry;
    this.host = host;
    this.rows = rows;
  }

  /** Read for every block on every change-detection pass, so it stays a few field reads. */
  canDrag(row: TimelineRow, task: Task): boolean {
    return (
      this.editingHere() &&
      !row.validated &&
      !!row.route &&
      task.kind === 'work' &&
      !!task.actionUid &&
      !isPredictedActionUid(task.actionUid) &&
      (task.jobUids?.length ?? 0) > 0
    );
  }

  /**
   * Whether the tour's band can be dragged: DL refuses a tour sharing a job with another, and
   * knows no predicted stop. `blocked` is the tour's `tourMoveBlocker`, for a caller holding it.
   */
  canDragTour(
    row: TimelineRow,
    tour: Tour,
    blocked = !!row.route && tourMoveBlocker(row.route, tour) !== null
  ): boolean {
    return (
      this.editingHere() &&
      !row.validated &&
      !!row.route &&
      !blocked &&
      !tour.actionUids.some(isPredictedActionUid)
    );
  }

  onBlockPointerDown(row: TimelineRow, task: Task, ev: PointerEvent): void {
    if (ev.button !== 0 || this.press || this.picked() || !this.canDrag(row, task)) return;
    const route = row.route!;
    const action = route.actions.find((a) => a.uid === task.actionUid);
    if (!action) return;
    const origin = pinOrigin(action);
    const earliestPin = earliestPinStart(route, action.uid);
    const blockOffsetMs = task.startDate.getTime() - origin.getTime();
    const block = ev.currentTarget as Element | null;
    const blockLeft = block?.getBoundingClientRect().left;
    const rowBox = block?.closest('.timeline-row')?.getBoundingClientRect();

    this.press = {
      row,
      carried: {
        kind: 'stop',
        routeUid: route.uid,
        actionUid: action.uid,
        siteUid: action.site_uid,
        jobUids: getHandledJobUids(action)
      },
      name: task.name,
      leaveBlocked: loadsJobsDeliveredOnItsRoute(route, action.uid),
      clientX: ev.clientX,
      grabOffset: blockLeft === undefined ? 0 : ev.clientX - blockLeft,
      from: task.startDate,
      to: task.endDate,
      origin,
      earliest: earliestPin && new Date(earliestPin.getTime() + blockOffsetMs),
      band: rowBox ? { top: rowBox.top, bottom: rowBox.bottom } : null,
      moved: false,
      dx: 0,
      clientY: ev.clientY,
      reorderTargets: null,
      moveTargets: new Map(),
      over: null
    };
    this.zone.runOutsideAngular(() => this.listen());
  }

  /** Whether a route's start marker can be dragged. */
  canDragStart(row: TimelineRow, task: Task): boolean {
    return (
      this.editingHere() &&
      !row.validated &&
      task.kind === 'start' &&
      !!row.route &&
      departureAction(row.route) !== null
    );
  }

  /** Presses a route's start marker: a sideways drag moves when the route leaves, with no wall. */
  onStartPointerDown(row: TimelineRow, task: Task, ev: PointerEvent): void {
    if (ev.button !== 0 || this.press || this.picked() || !this.canDragStart(row, task)) {
      return;
    }
    const route = row.route!;
    this.press = {
      row,
      carried: { kind: 'start', routeUid: route.uid },
      name: row.name,
      leaveBlocked: false,
      clientX: ev.clientX,
      grabOffset: 0,
      from: task.startDate,
      to: task.startDate,
      origin: actionStart(departureAction(route)!),
      earliest: null,
      band: null,
      moved: false,
      dx: 0,
      clientY: ev.clientY,
      reorderTargets: null,
      moveTargets: new Map(),
      over: null
    };
    this.zone.runOutsideAngular(() => this.listen());
  }

  /** Presses a tour's band: a drag moves the whole tour onto the row under the pointer. */
  onTourPointerDown(row: TimelineRow, tour: Tour, name: string, ev: PointerEvent): void {
    if (ev.button !== 0 || this.press || this.picked()) return;
    if (!this.canDragTour(row, tour)) return;
    const from = new Date(tour.start);
    const bandLeft = (ev.currentTarget as Element | null)?.getBoundingClientRect().left;
    this.press = {
      row,
      carried: { kind: 'tour', routeUid: row.route!.uid, actionUids: tour.actionUids },
      name,
      leaveBlocked: false,
      clientX: ev.clientX,
      grabOffset: bandLeft === undefined ? 0 : ev.clientX - bandLeft,
      from,
      to: new Date(tour.end),
      origin: from,
      earliest: null,
      band: null,
      moved: false,
      dx: 0,
      clientY: ev.clientY,
      reorderTargets: null,
      moveTargets: new Map(),
      over: null
    };
    this.zone.runOutsideAngular(() => this.listen());
  }

  /**
   * Picks up a stop, one of its jobs or a tour until a click places it: on a target it sends the
   * move, anywhere else — or on Esc or a right-click — it cancels. `name` is what the strip calls
   * a tour.
   */
  startPlacing(carried: CarriedItem, name = ''): void {
    this.cancel();
    this.picked.set({ carried, shown: this.placingShown(carried, name) });
    this.document.body.classList.add(PLACING_CLASS);
    this.zone.runOutsideAngular(() => this.listenWhilePlacing());
  }

  cancel(): void {
    this.end();
    this.endPlacing();
  }

  private placingShown(
    carried: CarriedItem,
    name: string
  ): Omit<EditPlacing, 'targets' | 'target' | 'overRowUid'> {
    if (carried.kind === 'tour') {
      const route = this.rows().find((row) => row.route?.uid === carried.routeUid)?.route;
      return {
        kind: 'tour',
        actionUid: carried.actionUids[0] ?? '',
        actionUids: carried.actionUids,
        allStops: !!route && routeStops(route).length === carried.actionUids.length,
        name,
        site: '',
        quantity: null
      };
    }
    const row = this.rows().find((candidate) => candidate.route?.uid === carried.routeUid);
    const task = row?.tasks.find(
      (t) => t.kind === 'work' && t.actionUid === carried.actionUid
    );
    const site = task?.name ?? carried.actionUid;
    if (carried.kind === 'stop') {
      return {
        kind: 'stop',
        actionUid: carried.actionUid,
        actionUids: [carried.actionUid],
        allStops: false,
        name: site,
        site,
        quantity: null
      };
    }
    const action = this.shownAction(carried);
    return {
      kind: 'job',
      actionUid: carried.actionUid,
      actionUids: [carried.actionUid],
      allStops: false,
      name: carried.jobUid,
      site,
      quantity: action ? jobQuantity(action, carried.jobUid) : null
    };
  }

  private listen(): void {
    this.listeners = new AbortController();
    const { signal } = this.listeners;
    const doc = this.document;
    doc.addEventListener('pointermove', (ev) => this.onMove(ev), { signal });
    doc.addEventListener('pointerup', () => this.zone.run(() => this.onUp()), { signal });
    doc.addEventListener('pointercancel', () => this.zone.run(() => this.end()), {
      signal
    });
    // Capture, so neither the plan page's Escape nor the timeline's context menu sees the key or
    // the right-click that cancels the drag.
    doc.addEventListener('keydown', (ev) => this.onKey(ev, true), {
      signal,
      capture: true
    });
    doc.addEventListener('keyup', (ev) => this.onKey(ev, false), { signal, capture: true });
    doc.addEventListener('contextmenu', (ev) => this.onContextMenu(ev), {
      signal,
      capture: true
    });
  }

  private onMove(ev: PointerEvent): void {
    const press = this.press;
    const geometry = this.geometry();
    if (!press || !geometry) return;
    const dx = ev.clientX - press.clientX;
    if (!press.moved && Math.abs(dx) < DRAG_THRESHOLD_PX) return;
    if (!press.moved) this.document.body.classList.add(GRABBING_CLASS);
    press.moved = true;
    press.dx = dx;
    press.clientY = ev.clientY;
    press.over = this.rowUnder(ev, press.over);
    this._altHeld.set(ev.altKey);

    const fromX = xOfDate(geometry, press.from);
    this.host?.style.setProperty(POINTER_X_PROPERTY, `${fromX + dx + press.grabOffset}px`);
    this.host?.style.setProperty(CARRY_X_PROPERTY, `${fromX + dx}px`);
    this.show(press, geometry);
  }

  /** The row whose task area is under the pointer; the last one found while it stays there. */
  private rowUnder(ev: MouseEvent, last: Over | null): Over | null {
    const lane = laneUnder(ev);
    if (!lane) return null;
    if (lane === last?.lane) return last;
    const uid = lane.getAttribute(EDIT_ROW_ATTRIBUTE);
    if (uid === NEW_ROUTE_ROW) return { lane, row: null };
    const row = this.rows().find((candidate) => candidate.uid === uid);
    return row ? { lane, row } : null;
  }

  /**
   * Sets `drag` from the pointer and the Alt key, unless it would show the same thing. Alt
   * reorders; without it the block moves over another route's task area, pins over its own and
   * lands nowhere off every route. A tour only moves, onto any route under the pointer, its own
   * included.
   */
  private show(press: Press, geometry: TimelineGeometry): void {
    const shown = this._drag();
    const { carried } = press;
    if (carried.kind === 'start') {
      const time = this.snappedTime(press, geometry);
      if (shown?.mode === 'start' && shown.time.getTime() === time.getTime()) return;
      this._drag.set({
        mode: 'start',
        carrying: 'start',
        routeUid: carried.routeUid,
        actionUid: '',
        actionUids: [],
        stopName: press.name,
        from: press.from,
        durationMs: 0,
        time,
        shiftMinutes: Math.round((time.getTime() - press.from.getTime()) / 60_000),
        earliest: null,
        targets: [],
        target: null,
        overRowUid: null,
        leaveBlocked: false
      });
      return;
    }
    const tour = carried.kind === 'tour';
    const actionUids = tour ? carried.actionUids : [carried.actionUid];
    const base = {
      carrying: carried.kind,
      routeUid: carried.routeUid,
      actionUid: actionUids[0] ?? '',
      actionUids,
      stopName: press.name,
      from: press.from,
      durationMs: press.to.getTime() - press.from.getTime(),
      earliest: press.earliest,
      leaveBlocked: press.leaveBlocked
    };

    if (!tour && this._altHeld()) {
      press.reorderTargets ??= drawnTargets(press.row, carried);
      const target = this.hoveredGap(press, geometry, press.reorderTargets);
      if (shown?.mode === 'reorder' && shown.target === target) return;
      this._drag.set({
        ...base,
        mode: 'reorder',
        time: shown?.time ?? press.from,
        shiftMinutes: shown?.shiftMinutes ?? 0,
        targets: press.reorderTargets,
        target,
        overRowUid: null
      });
      return;
    }

    if (press.over && press.over.row === null) {
      const targets = press.leaveBlocked ? [] : [NEW_ROUTE_EDIT_TARGET];
      const target = targets[0] ?? null;
      if (
        shown?.mode === 'move' &&
        shown.overRowUid === NEW_ROUTE_ROW &&
        shown.target === target
      ) {
        return;
      }
      this._drag.set({
        ...base,
        mode: 'move',
        time: press.from,
        shiftMinutes: 0,
        targets,
        target,
        overRowUid: NEW_ROUTE_ROW
      });
      return;
    }

    const over = press.over?.row;
    if (over && (tour || over.uid !== press.row.uid)) {
      let targets = press.moveTargets.get(over.uid);
      if (!targets) {
        targets = press.leaveBlocked ? [] : drawnTargets(over, carried);
        press.moveTargets.set(over.uid, targets);
      }
      const pointerX = xOfDate(geometry, press.from) + press.dx + press.grabOffset;
      // On its own row a tour's place is a candidate too, which hovers nothing: a band barely
      // moved, or brought back, reorders nothing.
      const reach = over.uid === press.row.uid ? Math.abs(press.dx) : Infinity;
      const target = targetAt(targets, pointerX, geometry, reach);
      if (
        shown?.mode === 'move' &&
        shown.overRowUid === over.uid &&
        shown.target === target
      ) {
        return;
      }
      this._drag.set({
        ...base,
        mode: 'move',
        time: press.from,
        shiftMinutes: 0,
        targets,
        target,
        overRowUid: over.uid
      });
      return;
    }

    if (tour || !press.over) {
      if (shown?.mode === 'move' && shown.overRowUid === null) return;
      this._drag.set({
        ...base,
        mode: 'move',
        time: press.from,
        shiftMinutes: 0,
        targets: [],
        target: null,
        overRowUid: null
      });
      return;
    }

    let time = this.snappedTime(press, geometry);
    if (press.earliest && time < press.earliest) time = press.earliest;
    if (shown?.mode === 'pin' && shown.time.getTime() === time.getTime()) return;

    this._drag.set({
      ...base,
      mode: 'pin',
      time,
      shiftMinutes: Math.round((time.getTime() - press.from.getTime()) / 60_000),
      targets: [],
      target: null,
      overRowUid: null
    });
  }

  /**
   * Where the pointer puts the pressed block or marker, snapped. Within half a snap of where it
   * started it stays put, so an off-grid start dragged out and back is not moved to the nearest
   * grid mark.
   */
  private snappedTime(press: Press, geometry: TimelineGeometry): Date {
    const raw = dateOfX(geometry, xOfDate(geometry, press.from) + press.dx);
    return Math.abs(raw.getTime() - press.from.getTime()) < SNAP_MS / 2
      ? press.from
      : snapToMinutes(raw, SNAP_MS / 60_000);
  }

  /**
   * The gap nearest the dragged block's centre, on its own row. The block's own place counts as
   * a candidate that hovers nothing, so a drag barely moved, or brought back, reorders nothing.
   */
  private hoveredGap(
    press: Press,
    geometry: TimelineGeometry,
    gaps: EditTarget[]
  ): EditTarget | null {
    const { band, clientY } = press;
    if (band && (clientY < band.top || clientY > band.bottom)) return null;
    const fromX = xOfDate(geometry, press.from);
    const width = Math.max(MIN_TASK_WIDTH_PX, xOfDate(geometry, press.to) - fromX);
    const home = fromX + width / 2;
    const centre = home + press.dx;
    let nearest: EditTarget | null = null;
    let distance = Math.abs(centre - home);
    for (const gap of gaps) {
      const d = Math.abs(centre - xOfDate(geometry, gap.at));
      if (d < distance) {
        nearest = gap;
        distance = d;
      }
    }
    return nearest;
  }

  private onUp(): void {
    const press = this.press;
    const drag = this._drag();
    if (press?.moved) this.swallowNextClick();
    this.end();
    if (!press || !drag) return;
    const { carried } = press;
    const shiftMs = drag.time.getTime() - press.from.getTime();
    const at = new Date(press.origin.getTime() + shiftMs).toISOString();

    if (carried.kind === 'start') {
      if (shiftMs !== 0) {
        this.request({ kind: 'set-route-start', routeUid: carried.routeUid, at });
      }
      return;
    }

    if (drag.mode === 'move') {
      if (drag.target)
        this.sendMove(carried, drag.target, press.over?.lane ?? null, press.name);
      return;
    }

    if (drag.mode === 'reorder' && carried.kind === 'stop') {
      const placement = drag.target?.placement;
      if (!placement || placement.kind === 'MergedInto') return;
      this.request({
        kind: 'reorder',
        routeUid: carried.routeUid,
        actionUid: carried.actionUid,
        placement
      });
      return;
    }

    if (shiftMs === 0 || carried.kind !== 'stop') return;
    this.request({
      kind: 'pin',
      routeUid: carried.routeUid,
      actionUid: carried.actionUid,
      at
    });
  }

  private request(gesture: PlanEditGesture): void {
    this.store.dispatch(PlanEditSessionActions.gestureRequested({ gesture }));
  }

  /**
   * Sends the move, or, onto the + New route row, opens the new route's form on `anchor`: its Apply
   * opens the route and moves `name` into it in one batch, its Cancel sends nothing.
   */
  private sendMove(
    carried: CarriedItem,
    target: EditTarget,
    anchor: Element | null,
    name: string
  ): void {
    const gesture = moveGesture(
      carried,
      target.placement,
      this.pins(),
      carried.kind === 'job' ? this.shownAction(carried) : undefined
    );
    if (!gesture) return;
    if (target.routeUid !== NEW_ROUTE_ROW) {
      this.request(gesture);
    } else if (anchor instanceof HTMLElement) {
      this.forms.open(anchor, { kind: 'new-route', then: gesture, carriedName: name });
    }
  }

  /** The carried item's action as shown now, which a patch may have changed since it was picked. */
  private shownAction(carried: CarriedStop | CarriedJob) {
    return this.rows()
      .find((row) => row.route?.uid === carried.routeUid)
      ?.route?.actions.find((a) => a.uid === carried.actionUid);
  }

  private listenWhilePlacing(): void {
    this.placingListeners = new AbortController();
    const { signal } = this.placingListeners;
    const doc = this.document;
    doc.addEventListener('pointermove', (ev) => this.onPlacingMove(ev), { signal });
    // Capture, so the click that places or cancels reaches nothing else: not the row, whose
    // click filters the timeline, nor the block, whose click opens its popup.
    doc.addEventListener(
      'click',
      (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        this.zone.run(() => this.place(ev));
      },
      { signal, capture: true }
    );
    doc.addEventListener(
      'keydown',
      (ev) => {
        if (ev.key !== 'Escape') return;
        ev.preventDefault();
        ev.stopPropagation();
        this.zone.run(() => this.endPlacing());
      },
      { signal, capture: true }
    );
    doc.addEventListener(
      'contextmenu',
      (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        this.zone.run(() => this.endPlacing());
      },
      { signal, capture: true }
    );
    doc.addEventListener('scroll', () => (this.laneLeft = null), {
      signal,
      capture: true,
      passive: true
    });
  }

  private onPlacingMove(ev: PointerEvent): void {
    const next = this.placingHoverAt(ev);
    const shown = this.hover();
    if (shown.rowUid === next.rowUid && shown.target === next.target) return;
    this.hover.set(next);
  }

  private placingHoverAt(ev: MouseEvent): Hover {
    const lane = laneUnder(ev);
    const geometry = this.geometry();
    const targets = this.placingTargets();
    if (!lane || !geometry || !targets) return NO_HOVER;
    if (lane !== this.lane || this.laneLeft === null) {
      this.lane = lane;
      this.laneLeft = lane.getBoundingClientRect().left;
    }
    const x = ev.clientX - this.laneLeft;
    this.host?.style.setProperty(POINTER_X_PROPERTY, `${x}px`);
    const rowUid = lane.getAttribute(EDIT_ROW_ATTRIBUTE);
    const candidates = targets[rowUid ?? ''] ?? [];
    return {
      rowUid,
      target:
        rowUid === NEW_ROUTE_ROW
          ? (candidates[0] ?? null)
          : targetAt(candidates, x, geometry, PLACE_REACH_PX)
    };
  }

  private place(ev: MouseEvent): void {
    const picked = this.picked();
    const { target } = this.placingHoverAt(ev);
    this.endPlacing();
    if (picked && target) {
      this.sendMove(picked.carried, target, laneUnder(ev), picked.shown.name);
    }
  }

  private endPlacing(): void {
    this.placingListeners?.abort();
    this.placingListeners = null;
    this.lane = null;
    this.laneLeft = null;
    this.document.body.classList.remove(PLACING_CLASS);
    this.picked.set(null);
    this.hover.set(NO_HOVER);
  }

  private onKey(ev: KeyboardEvent, down: boolean): void {
    if (ev.key === 'Alt') {
      // Alone, Alt's release focuses the browser's menu on Windows and Linux.
      ev.preventDefault();
      this._altHeld.set(down);
      const geometry = this.geometry();
      if (this.press?.moved && geometry) this.show(this.press, geometry);
    } else if (down && ev.key === 'Escape' && this.press) {
      ev.preventDefault();
      ev.stopPropagation();
      this.zone.run(() => this.abort());
    }
  }

  private onContextMenu(ev: MouseEvent): void {
    ev.preventDefault();
    ev.stopPropagation();
    this.zone.run(() => this.abort());
  }

  /** Ends the press without sending, while the button may still be held. */
  private abort(): void {
    if (this.press?.moved) this.swallowClickOfRelease();
    this.end();
  }

  private end(): void {
    this.listeners?.abort();
    this.listeners = null;
    this.press = null;
    this.document.body.classList.remove(GRABBING_CLASS);
    this._drag.set(null);
    this._altHeld.set(false);
  }

  /**
   * A release away from where the press began clicks their common ancestor: the row, whose click
   * filters the timeline on its route. The click follows the pointerup within the same task, so a
   * listener dropped on the next one catches only that click.
   */
  private swallowNextClick(): void {
    const signal = this.guardClicks();
    this.document.defaultView!.addEventListener(
      'click',
      (ev) => {
        ev.stopPropagation();
        ev.preventDefault();
      },
      { capture: true, once: true, signal }
    );
    setTimeout(() => {
      if (this.clickGuard?.signal === signal) this.clickGuard.abort();
    });
  }

  /** A cancelled drag's button is still down: swallow the click of its release. */
  private swallowClickOfRelease(): void {
    this.document.defaultView!.addEventListener(
      'pointerup',
      () => this.swallowNextClick(),
      {
        capture: true,
        once: true,
        signal: this.guardClicks()
      }
    );
  }

  private guardClicks(): AbortSignal {
    this.clickGuard?.abort();
    this.clickGuard = new AbortController();
    return this.clickGuard.signal;
  }
}

function laneUnder(ev: MouseEvent): Element | null {
  return ev.target instanceof Element ? ev.target.closest(`[${EDIT_ROW_ATTRIBUTE}]`) : null;
}

/**
 * The target under the pointer: a merge while it is over that stop, else the nearest gap or end
 * within `reach` pixels.
 */
function targetAt(
  targets: EditTarget[],
  x: number,
  geometry: TimelineGeometry,
  reach: number
): EditTarget | null {
  let nearest: EditTarget | null = null;
  let distance = reach;
  for (const target of targets) {
    if (target.label === 'merge') {
      if (!target.stop) continue;
      const left = xOfDate(geometry, target.stop.start);
      const right = Math.max(left + MIN_TASK_WIDTH_PX, xOfDate(geometry, target.stop.end));
      if (x >= left && x <= right) return target;
      continue;
    }
    const d = Math.abs(x - xOfDate(geometry, target.at));
    if (d < distance) {
      nearest = target;
      distance = d;
    }
  }
  return nearest;
}

/** The places of one shown route a carried stop can land, where the timeline draws them. */
function drawnTargets(row: TimelineRow, carried: CarriedItem): EditTarget[] {
  const route = row.route;
  if (!route) return [];
  const blocks = new Map<string, Task>();
  for (const t of row.tasks) {
    if (t.kind === 'work' && t.actionUid) blocks.set(t.actionUid, t);
  }
  const midway = (a: Date, b: Date) => new Date((a.getTime() + b.getTime()) / 2);
  const stopOf = (block: Task) => ({
    name: block.name,
    start: block.startDate,
    end: block.endDate
  });
  const lastBlock = routeStops(route)
    .reverse()
    .find((stop) => blocks.has(stop.uid));
  const lastEnd = lastBlock ? blocks.get(lastBlock.uid)!.endDate : row.startDate;

  const targets: EditTarget[] = [];
  for (const target of positionsFor(
    { uid: row.uid, route, validated: !!row.validated },
    carried
  )) {
    const placement = target.placement;
    if (placement.kind === 'EndOf') {
      targets.push({ ...target, at: midway(lastEnd, row.endDate), stop: null });
      continue;
    }
    const block = blocks.get(placement.actionUid);
    if (!block) continue;
    if (placement.kind === 'MergedInto') {
      targets.push({
        ...target,
        at: midway(block.startDate, block.endDate),
        stop: stopOf(block)
      });
      continue;
    }
    const i = route.actions.findIndex((a) => a.uid === placement.actionUid);
    const previousEnd =
      blocks.get(route.actions[i - 1]?.uid ?? '')?.endDate ?? row.startDate;
    targets.push({
      ...target,
      at: midway(previousEnd, block.startDate),
      stop: stopOf(block)
    });
  }
  return targets;
}
